import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Plus } from "lucide-react";
import { toast } from "sonner";

const Notifications = () => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", audience: "all" });

  const canBroadcast = ["super_admin", "director", "teacher", "receptionist"].includes(user?.role);

  const load = async () => {
    try { const { data } = await api.get("/notifications"); setList(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/notifications", form);
      setList((l) => [data, ...l]); setOpen(false); setForm({ title: "", body: "", audience: "all" });
      toast.success("Notification published");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="notifications-page">
      <PageHeader
        overline="Communications"
        title="Notifications & Reminders"
        description="Broadcast announcements to parents, students or specific groups across the academy."
        actions={canBroadcast && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="new-notif-btn"><Plus className="w-4 h-4 mr-1.5" /> Broadcast</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">New Announcement</DialogTitle></DialogHeader>
              <div className="grid gap-4 mt-2">
                <Fld label="Title"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notif-title" /></Fld>
                <Fld label="Body"><Textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="notif-body" /></Fld>
                <Fld label="Audience">
                  <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                    <SelectTrigger data-testid="notif-audience"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="students">Students</SelectItem>
                      <SelectItem value="parents">Parents</SelectItem>
                      <SelectItem value="teachers">Teachers</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                </Fld>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="notif-submit">Publish</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      {list.length === 0 ? <EmptyState message="No notifications yet." /> : (
        <div className="grid gap-3">
          {list.map(n => (
            <Card key={n.id} className="p-5 border-[#E2E8F0] hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`notif-${n.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#9DCD3F]/15 text-[#9DCD3F] grid place-items-center"><Bell className="w-5 h-5" /></div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-lg text-[#1B2C5A]">{n.title}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#FDFBF7] border border-[#E2E8F0] capitalize">{n.audience}</span>
                  </div>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed whitespace-pre-line">{n.body}</p>
                  <div className="text-xs text-[#475569] mt-3">By {n.created_by_name} · {new Date(n.created_at).toLocaleString()}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

export default Notifications;
